import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, filter, map, switchMap } from 'rxjs/operators';
import { ITableDataGroupRow, ITableDataHostDataChangedArg, ITableDataHostOptions, ITableDataLayoutOptions } from 'src/app/reports/table-data/table-data.component';
import { ITableDataResponse } from 'src/app/reports/table-data/table-data.service';
import { IPageDataViewModel } from 'src/app/share/pages/pages';
import { AuthService } from 'src/app/share/services/auth.service';
import { CommonService } from 'src/app/share/services/common.service';
import { DateUtil } from 'src/app/share/utils/date-util';

@Component({
  selector: 'app-live-trips-page',
  templateUrl: './live-trips-page.component.html',
  styleUrls: ['./live-trips-page.component.scss']
})
export class LiveTripsPageComponent implements OnInit {

  constructor(private commonService: CommonService, private authService: AuthService, private http: HttpClient) { }

  vm$: Observable<IViewModel>

  private readonly apiPath: string = '/api/external-customer';

  ngOnInit() {
    this.vm$ = this.pageLoad();
  }

  /**
   *
   */
  refreshPage() {
    this.vm$ = this.pageLoad();
  }

  /**
   *
   * @param event
   */
  async onSelectMap(vm: IViewModel) {
    if (!vm.mapVm.requiresLoad)
      return;

    // load the map
    try {
      vm.mapVm.errorText = undefined;
      vm.mapVm.workingText = 'Loading data...';
      vm.mapVm.centerPoint = undefined;

      const confirmNumber = vm.confirms.map(c => c.confirmNumber);

      // load the data
      const apiResult = await of({}).pipe(
        delay(100),
        switchMap(() => this.commonService.getData('PageCommand', 'live-trips', 'map-show-trips', { confirmNumbers: confirmNumber }, this.apiPath)),
        filter(result => !result.workingText),
        map(result => ({ errorText: result.errorText, data: <IApiMapShowTripsResult>result.data}))
      ).toPromise();

      if (apiResult.errorText) {
        // handle api error
        vm.mapVm.errorText = apiResult.errorText;

      } else if (apiResult.data) {

        const markerItems = (apiResult.data.segments || []).map(s => {
          return {
            segment: s,
            groupRow: vm.confirms.find(c => c.confirmNumber == s.confirmNumber)?.row
          };
        });

        // handle api data
        vm.mapVm.markers = markerItems.map(mi => {
          const marker: IMapMarker = {
            latitude: mi.segment.latitude, longitude: mi.segment.longitude,
            title: mi.segment.confirmNumber,

            details: mi.groupRow.cells.map(c => {
              return {
                title: c.columnName,
                rows: [...c.cellRows]
              };
            })
          };
          return marker;
        });
        vm.mapVm.centerPoint = vm.mapVm.markers.length == 0 ? undefined
          : {
            latitude: vm.mapVm.markers.map(m => m.latitude).sort((a, b) => Math.abs(a) - Math.abs(b))[0] /* lowest */,
            longitude: vm.mapVm.markers.map(m => m.longitude).sort((a, b) => Math.abs(b) - Math.abs(a) )[0] /* highest */
          };
        vm.mapVm.requiresLoad = false;
      }

    } catch (err) {
      vm.mapVm.errorText = err;
    } finally {
      vm.mapVm.workingText = undefined;
    }
  }

  /**
   *
   * @param vm
   * @param arg
   */
  private tableDataLoaded(vm: IViewModel, arg: ITableDataHostDataChangedArg) {
    const confirmNumberNdx = arg.columns.findIndex(c => c == 'Conf #');
    if (confirmNumberNdx < 0)
      return;

    const confirmNumbers = arg.filteredRows.reduce((pv, cv) => {
      return [
        ...pv, cv[confirmNumberNdx]
      ]
    }, []);

    if (vm.mapVm)
      vm.mapVm.requiresLoad = true;

    vm.confirms = confirmNumbers.map(cf => {
      const ret = {
        confirmNumber: cf,
        row: arg.groupedRows.find(gr => gr.cells.find(c => c.cellRows.find(cr => cr.title == 'CF #' && cr.value == cf)))
      };
      return ret;
    });
  }

  /**
   *
   * @returns
   */
  private pageLoad(): Observable<IViewModel> {
    return this.commonService.getData('PageCommand', 'live-trips', 'page-load', {}, this.apiPath).pipe(
      map((v: IViewModel) => {
        if (v.workingText)
          return { ...v };

        const ret: IViewModel = {
          ...v, actionsAllowed: true,

          isTourManager: this.authService.isCustomerRoleTours(),
          title: this.authService.isCustomerRoleTours() ? 'Tours Manifest' : 'Manifest',

          confirms: [],

          gridVm: {
            hostOptions: {
              reportCode: this.authService.isCustomerRoleTours() ? 'tours-manifest' : 'event-manifest',
              paramOverrides: {
                startDate: DateUtil.isoDateString(DateUtil.now()),
                CustomersList: this.authService.isCustomerRoleTours() && (this.authService.getParentGroupCodes() ?? '').length == 0 ? this.authService.getCustomerCodes() : '',
                parentGroup: this.authService.getParentGroupCodes().length > 0 ? this.authService.getParentGroupCodes() : ''
              },
              paramHidden: {
                eventCode: true,
                customerCode: true,
                endDate: true,
                parentGroup: true,
              },
              serviceOptions: {
                controller: 'external-customer',
                externalLoginTicket: undefined
              },
              tableDataChanged: (arg) => this.tableDataLoaded(ret, arg)
            },
            layoutOptions: {
              titleOverride: '' /* pull from context if we want to show customer name or something */
            }
          }
        };

        if (ret.data) {
          ret.mapVm = undefined; /*{
            requiresLoad: true,
            centerPoint: undefined,
            markers: []

            // ...this.mapVmSample()
          };*/
        }

        return ret;
      })
    );
  }

  /**
   *
   * @returns
   */
  private mapVmSample(): IMapViewModel {
    const ret: IMapViewModel = {
      requiresLoad: false,
      disclaimerText: 'Snapshot of data from 3/17/23 9:00 AM',
      centerPoint: { latitude: 40.766988, longitude: -73.963947 },
      markers: [
        { latitude: 40.766988, longitude: -73.963947 },
        { latitude: 40.766988, longitude: -73.963947 },
        { latitude: 40.764602, longitude: -73.968584 },
        { latitude: 40.761136, longitude: -73.973218 },
        { latitude: 41.046601, longitude: -73.921665 },
        { latitude: 40.761726, longitude: -73.979032 },
        { latitude: 40.787420, longitude: -74.047088 },
        { latitude: 40.781028, longitude: -74.080483 },
        { latitude: 40.757946, longitude: -73.975341 },
        { latitude: 40.904625, longitude: -74.030501 },
        { latitude: 40.770568, longitude: -73.968360 },
        { latitude: 40.762776, longitude: -73.978550 },
        { latitude: 40.764703, longitude: -73.881367 },
        { latitude: 40.726040, longitude: -73.991933 },
        { latitude: 40.757903, longitude: -73.975749 },
        { latitude: 40.770891, longitude: -73.983500 },
        { latitude: 40.771873, longitude: -73.981380 },
        { latitude: 40.732331, longitude: -74.180726 },
        { latitude: 40.748843, longitude: -73.985548 },
        { latitude: 40.774150, longitude: -73.963031 },
        { latitude: 40.768183, longitude: -73.862837 },
        { latitude: 41.029781, longitude: -73.955765 },
        { latitude: 40.712489, longitude: -74.009167 },
        { latitude: 40.645213, longitude: -73.783418 },
        { latitude: 40.793783, longitude: -74.013636 },
        { latitude: 40.745464, longitude: -73.989129 },
        { latitude: 40.772802, longitude: -73.985905 },
        { latitude: 40.836487, longitude: -73.838975 },
        { latitude: 40.766671, longitude: -73.990321 }
      ].map(m => ({ ...m, title: 'test', details: undefined }))

    };
    return ret;
  }

}

/**
 *
 */
interface IViewModel extends IPageDataViewModel {
  confirms: {
    confirmNumber: string;
    row: ITableDataGroupRow;
  }[];

  isTourManager?: boolean;
  title: string;

  gridVm?: IGridViewModel;
  mapVm?: IMapViewModel;
}

/**
 *
 */
interface IGridViewModel {
  hostOptions: ITableDataHostOptions;
  layoutOptions: ITableDataLayoutOptions;
}

/**
 *
 */
interface IMapViewModel {
  requiresLoad: boolean;
  disclaimerText?: string;

  workingText?: string;
  errorText?: string;

  centerPoint: IMapLatLng;
  markers: IMapMarker[];
}

/**
 *
 */
interface IMapLatLng {
  latitude: number;
  longitude: number;
}

/**
 *
 */
interface IMapMarker extends IMapLatLng {
  title?: string;

  details: {
    title: string;
    rows: { title: string, value: string }[];
  }[];
}

/**
 *
 */
interface IApiMapShowTripsResult {
  segments: {
    confirmNumber: string;
    vehicleCode: string;
    pickupState: string;
    tripStatus: string;
    pickupDateTime: string;
    latitude: number;
    longitude: number;
  }[];
}
